/** Formateo para lectura humana. Todo en es-ES con separadores de miles. */

const NUMBER = new Intl.NumberFormat('es-ES');

export function formatNumber(value: number): string {
  return NUMBER.format(value);
}

/**
 * Bytes en unidades binarias (KiB, MiB…) etiquetadas como KB/MB, que es lo que muestra
 * RouterOS. Mantener la misma convención que el router evita que el operador vea dos
 * cifras distintas para el mismo dato.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  const decimals = exponent === 0 ? 0 : value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(decimals)} ${units[exponent]}`;
}

/** Duración compacta: `2d 4h`, `3h 12m`, `45s`. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const total = Math.floor(seconds);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

export function formatRemainingSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "sin límite";
  if (seconds <= 0) return "vencido";
  return formatDuration(seconds);
}

export function formatMinutes(minutes: number): string {
  if (minutes <= 0) return 'Sin límite';
  if (minutes % 1440 === 0) {
    const days = minutes / 1440;
    return days === 1 ? '1 día' : `${days} días`;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 hora' : `${hours} horas`;
  }
  return `${minutes} min`;
}

export function formatKbps(kbps: number | null | undefined): string {
  if (!kbps) return '—';
  if (kbps >= 1000) {
    const mbps = kbps / 1000;
    return `${Number.isInteger(mbps) ? mbps : mbps.toFixed(1)} Mbps`;
  }
  return `${kbps} kbps`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** "hace 3 min" / "en 2 días". Devuelve `—` para valores ausentes. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const deltaSeconds = (date.getTime() - Date.now()) / 1000;
  const absolute = Math.abs(deltaSeconds);
  const formatter = new Intl.RelativeTimeFormat('es-ES', { numeric: 'auto' });

  if (absolute < 60) return formatter.format(Math.round(deltaSeconds), 'second');
  if (absolute < 3600) return formatter.format(Math.round(deltaSeconds / 60), 'minute');
  if (absolute < 86400) return formatter.format(Math.round(deltaSeconds / 3600), 'hour');
  if (absolute < 2_592_000) return formatter.format(Math.round(deltaSeconds / 86400), 'day');
  return formatDate(value);
}

export function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(price);
  } catch {
    // Una moneda no reconocida por ICU no debe romper la tabla.
    return `${price.toFixed(2)} ${currency}`;
  }
}

/** `datetime-local` necesita `YYYY-MM-DDTHH:mm` en hora local, no ISO en UTC. */
export function toDateTimeInput(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function fromDateTimeInput(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
